import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createContext, forwardRef, useContext } from 'react';
import { SkeletonDefault } from '../../skeleton/skeleton-default.js';
import { Skeleton } from '../../ui/skeleton.js';
import { cn } from '../../../lib/utils.js';

const InfoContext = createContext(null);
const InfoListGroup = forwardRef(
  ({ isLoading = false, type = "round", className, children, onClick }, ref) => {
    return /* @__PURE__ */ jsx(InfoContext.Provider, { value: { type, isLoading }, children: /* @__PURE__ */ jsx(
      "section",
      {
        className: cn(
          "round-box round-box--type1 p-5",
          type === "dashed" && "border border-dashed border-basic-03 bg-basic-background-07 p-4 rounded-[8px]",
          (type === "default" || type === "minimum") && "p-0 !bg-transparent-white !border-none",
          type === "minimumBox" && "p-4 md:p-5 minimumBox",
          className
        ),
        role: onClick ? "button" : void 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter") {
            onClick?.();
          }
        },
        tabIndex: onClick ? 0 : -1,
        ref,
        children: isLoading && !children ? /* @__PURE__ */ jsx(SkeletonDefault, { col: 3 }) : children
      }
    ) });
  }
);
const InfoListItemSkeleton = () => {
  return /* @__PURE__ */ jsx(Skeleton, { className: "w-full h-5 mt-3 first:mt-0" });
};
function renderEntities(str) {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}
const InfoListItem = ({
  title,
  boldTitle,
  desc,
  titleFit,
  descFit,
  className,
  color = "default",
  size = "large"
}) => {
  const info = useContext(InfoContext);
  return /* @__PURE__ */ jsx(
    "section",
    {
      className: cn(
        "item flex gap-5 items-start justify-between border-b border-b-basic-05 py-4 last:border-b-0 first:pt-0 last:pb-0",
        (info?.type === "minimum" || info?.type === "dashed") && "py-0 border-none mt-3 first:mt-0",
        info?.type === "minimumBox" && "py-0 border-none mt-3 first:mt-0",
        className
      ),
      children: info?.isLoading ? /* @__PURE__ */ jsx(InfoListItemSkeleton, {}) : /* @__PURE__ */ jsxs(Fragment, { children: [
        title && /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "text-basic-04 break-words info-tit",
              size === "large" && "body-lg",
              (size === "medium" || info?.type === "dashed" || info?.type === "minimumBox") && "body-md",
              boldTitle && "body-lg font-weight-bold text-basic-02",
              titleFit && "min-w-[fit-content]"
            ),
            children: typeof title === "string" ? renderEntities(title) : title
          }
        ),
        desc && /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "text-basic-02 font-weight-medium text-right min-w-0 info-txt",
              size === "large" && "body-lg",
              (size === "medium" || info?.type === "dashed" || info?.type === "minimumBox") && "body-md",
              color == "primary" && "text-primary-04",
              color === "danger" && "text-state-danger",
              descFit && "min-w-[fit-content]"
            ),
            children: desc
          }
        )
      ] })
    }
  );
};

export { InfoListGroup, InfoListItem };
